import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { ALL_TASKS, getTaskUrl } from '../constants/tasks';

export interface SearchResult {
  page: string;
  section: string;
  content: string;
  path: string;
  sectionId?: string;
}

interface SearchContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
  clearSearchWithAnimation: () => void;
  searchResults: SearchResult[];
  totalResults: number;
  isExiting: boolean;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

// Global search data - only actual learning content (tasks, exercises, quizzes)
const GLOBAL_SEARCH_DATA: SearchResult[] = ALL_TASKS.map(task => ({
  page: task.section.charAt(0).toUpperCase() + task.section.slice(1),
  section: task.title,
  content: task.description,
  path: getTaskUrl(task),
  sectionId: task.id
}));

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExiting, setIsExiting] = useState(false);

  const clearSearch = () => {
    setSearchTerm('');
    setIsExiting(false);
  };

  const clearSearchWithAnimation = () => {
    setIsExiting(true);
    // Clear search after animation completes
    setTimeout(() => {
      setSearchTerm('');
      setIsExiting(false);
    }, 200); // Match the CSS animation duration
  };

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) {
      return [];
    }
    
    const term = searchTerm.toLowerCase();
    const results = GLOBAL_SEARCH_DATA.filter(item => 
      item.content.toLowerCase().includes(term) ||
      item.page.toLowerCase().includes(term) ||
      item.section.toLowerCase().includes(term)
    );
    return results;
  }, [searchTerm]);

  const totalResults = searchResults.length;

  return (
    <SearchContext.Provider value={{ 
      searchTerm, 
      setSearchTerm, 
      clearSearch,
      clearSearchWithAnimation,
      searchResults, 
      totalResults,
      isExiting
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
